import type { Project } from "@/api/client";
import { isComicAnalysis, isLegacyAnalysis } from "@/api/client";
import { StatusBadge } from "./StatusBadge";
import { formatDistanceToNow } from "@/lib/utils";
import { Link } from "wouter";
import { ArrowRight, ImageIcon, Trash2 } from "lucide-react";

interface ProjectCardProps {
  project: Project;
  onDelete?: (id: string) => void;
}

export function ProjectCard({ project, onDelete }: ProjectCardProps) {
  // Get image count based on analysis type
  const imageCount = project.analysis 
    ? isLegacyAnalysis(project.analysis) 
      ? project.analysis.imageCount 
      : isComicAnalysis(project.analysis) 
        ? project.analysis.panels.length 
        : 0
    : 0;
  
  return (
    <div className="group relative bg-card rounded-xl border border-border overflow-hidden transition-all duration-200 hover:shadow-lg hover:border-primary/30">
      {/* Header */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="font-semibold text-lg text-foreground truncate">
            {project.name}
          </h3>
          <StatusBadge status={project.status} />
        </div>
        
        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
          {project.inputText}
        </p>
        
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <ImageIcon className="w-4 h-4" />
            <span>{imageCount} 张图片</span>
          </div>
          <span>•</span>
          <span>{formatDistanceToNow(new Date(project.createdAt))}</span>
        </div>
      </div>
      
      {/* Footer */}
      <div className="px-5 py-3 bg-muted/50 border-t border-border flex items-center justify-between">
        <Link
          href={`/projects/${project.id}`}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
        >
          查看详情
          <ArrowRight className="w-4 h-4" />
        </Link>
        
        {onDelete && (
          <button
            onClick={(e) => {
              e.preventDefault();
              onDelete(project.id);
            }}
            className="p-2 text-muted-foreground hover:text-destructive rounded-lg transition-colors opacity-0 group-hover:opacity-100"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
